import { useEffect, useState } from 'react';
import { Text, Stack, Link } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { PATHS } from 'router/routes';
import { stripeKey } from 'utils/constants';
import CreateAgentFormWrapper from '../components/CreateAgentFormWrapper';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import CheckoutForm from './CheckoutForm';
import Plans from './Plans';
import useAvailablePlans from 'hooks/useAvailablePlans';

const TRIAL_DAYS = 7;
const SubscriptionPlan = () => {
    const navigate = useNavigate();

    const [priceId, setPriceId] = useState('');

    const stripePromise = loadStripe(stripeKey);
    const { activeProducts, subscriptions, loading: plansLoading } = useAvailablePlans();

    useEffect(() => {
        if (activeProducts.length > 0) {
            setPriceId(activeProducts[activeProducts.length - 1].priceId);
        }
    }, [activeProducts]);

    const selectedProduct = activeProducts.find((product) => product.priceId === priceId);
    const firstSubscription = subscriptions.length > 0 ? subscriptions[0] : null;
    const options = selectedProduct ? {
        mode: 'subscription',
        amount: (selectedProduct.priceAmount ?? 0) * 100,
        currency: 'usd',
    } : undefined;

    const TrialText = ({ selectedProduct }) => {
        if (!selectedProduct) return <></>;
        if ((firstSubscription && firstSubscription.status === 'canceled') || (firstSubscription && firstSubscription.cancel_at_period_end)) {
            return (
                <div className='bg-yellow-50 border border-yellow-100 rounded-md p-2 text-xs text-yellow-800'>
                    As you are renewing a cancelled subscription, you will not receive a free trial.
                </div>
            );
        }
        const calculateBillingDate = (trialDays) => {
            const currentDate = new Date();
            const futureDate = new Date(currentDate);
            futureDate.setDate(currentDate.getDate() + trialDays);
            const month = String(futureDate.getMonth() + 1).padStart(2, '0');
            const day = String(futureDate.getDate()).padStart(2, '0');
            const year = futureDate.getFullYear();
            return `${month}/${day}/${year}`;
        };
        const billingDate = calculateBillingDate(TRIAL_DAYS);
        return (
            <div className='bg-blue-50 border border-blue-100 rounded-md p-2 text-xs text-blue-800'>
                After your trial ends, you will be charged ${selectedProduct.priceAmount} per month starting {billingDate}. You can always cancel before then.
            </div>
        );
    };

    if (!plansLoading && firstSubscription) {
        if ((firstSubscription.status === 'active' && !firstSubscription.cancel_at_period_end) || (firstSubscription.status === 'trialing' && !firstSubscription.cancel_at_period_end)) {
            navigate(PATHS.MY_AGENT);
            return null;
        }

    }
    if (plansLoading) {
        return (
            <CreateAgentFormWrapper
                title='Choose Your Plan'
                description='Start a trial to reserve a phone number for your agent'
            >
                <div>Loading plans...</div>
            </CreateAgentFormWrapper>
        );
    }

    return (
        <CreateAgentFormWrapper
            title='Choose Your Plan'
            description='Start a trial to reserve a phone number for your agent'
        >
            <Elements stripe={stripePromise} options={options}>
                <div className='w-full max-w-none grid gap-2 md:grid-cols-2 lg:grid-cols-3'>
                    {activeProducts.map((product) => (
                        <Plans
                            key={product.id}
                            product={product}
                            priceId={priceId}
                            setPriceId={setPriceId}
                        />
                    ))}
                </div>
                <Text fontWeight='bold'>Cancel anytime • No contract</Text>
                <Stack spacing='5'>
                    <Text className='text-sm text-[#71717a]'>
                        Need more than 500 minutes/mo?{' '}
                        <Link
                            href='mailto:contact@dialzara.com?subject=Request%20for%20additional%20pricing%20options'
                            color='purple.600'
                        >
                            Contact us for additional pricing.
                        </Link>
                    </Text>
                    <CheckoutForm priceId={priceId} trialText={<TrialText selectedProduct={selectedProduct} subscription={firstSubscription} />} subscription={firstSubscription} />
                </Stack>
            </Elements>
        </CreateAgentFormWrapper>
    );
};

export default SubscriptionPlan;
